import { isEmpty, isString } from 'lodash';

import { convertDateAndTimeToDisplayFormat } from '../DateHelper';
import { formatNumber, toSentenceCase } from '../GeneralHelper';

export abstract class DisplayController<Model, Display> {
	protected defaultStr = '-';
	/**
	 * Labels of the fields
	 */
	labels: Record<keyof Display, string>;
	/**
	 * Flags for displaying the fields
	 */
	visible: Record<keyof Display, boolean>;
	/**
	 * Formatted values of the fields
	 */
	values: Display;

	constructor(data: Model | Model[]) {
		const mergedData = Array.isArray(data) ? this.mergeDataObjects(data) : data;
		this.labels = this.getLabels();
		this.visible = this.visibilityRules(mergedData);
		this.values = this.transformData(mergedData);
	}

	protected abstract getLabels(): typeof this.labels;
	protected abstract visibilityRules(data: Model): typeof this.visible;
	protected abstract transformData(data: Model): typeof this.values;

	/**
	 * Check if value is null or empty
	 * @param value
	 * @returns {boolean}
	 */
	protected isEmptyValue(value: unknown) {
		if (typeof value == 'string') {
			return value.trim().length === 0;
		}
		if (typeof value == 'number') {
			return false;
		}
		return isEmpty(value);
	}

	protected isEmptyDate(dateValue?: string) {
		const invalidDates = ['0001-01-01T00:00:00', '1901-01-01T00:00:00'];
		if (!dateValue || typeof dateValue !== 'string' || invalidDates.includes(dateValue)) {
			return true;
		} else {
			return convertDateAndTimeToDisplayFormat(dateValue) === '';
		}
	}

	/**
	 * Replaces the unit in a given string with a specified unit, while also formatting the number to a specified number of decimal places.
	 * @param {string | undefined} str The input string that may contain a number and a unit.
	 * @param {string} unit The unit that should replace the existing unit in the string.
	 * @param {number} decimals The number of decimal places to format the number to.
	 * @returns The modified string with the replaced unit, or an empty string if the conditions are not met.
	 */
	protected replaceUnit(str: string | undefined, unit: string, decimals: number) {
		const defaultResult = '';
		try {
			if (str) {
				const split = str.split(' ');
				if (split.length === 2) {
					const formattedNumStr = formatNumber(split[0], decimals);
					return [formattedNumStr, unit].join(' ');
				}
			}
		} catch {}
		return defaultResult;
	}

	/**
	 * Compares two strings,  str1  and  str2 , after removing leading and trailing whitespace and converting them to lowercase.
	 * @param {string} str1 The first string to compare.
	 * @param {string} str2 The second string to compare.
	 * @returns {boolean}  true  if the strings are equal,  false  otherwise. If either of the input parameters is not a valid string, it returns  false .
	 */
	protected stringComparison(str1: string, str2: string) {
		if (isString(str1) && isString(str2)) {
			return str1.trim().toLowerCase() === str2.trim().toLowerCase();
		} else {
			return false;
		}
	}

	/**
	 * Removes leading numbers followed by a dot and whitespace from the given string.
	 *
	 * @param {string} string The string from which to remove the leading numbers.
	 * @returns {string} The modified string with leading numbers removed.
	 */
	protected removeNumberFromString(string: string) {
		return string.replace(/^\d+\.\s*/, '');
	}

	/**
	 * Converts a given ISO string to a specific display format.
	 * @param {string} isoString The ISO string representing a date and time value.
	 * @param {('date' | 'datetime' | 'datetimeZone' | 'datehhmm' | 'time')} type The desired format for the output. Defaults to 'date'.
	 * @returns {string} The converted string in the specified display format.
	 */
	protected convertTimeToDisplayFormat(
		isoString: string,
		type: 'date' | 'datetime' | 'datetimeZone' | 'datehhmm' | 'time' = 'date',
	) {
		return convertDateAndTimeToDisplayFormat(isoString, type);
	}

	/**
	 * Formats a given string value as a number with a specified number of decimals.
	 * @param {string} value The string value to format as a number.
	 * @param {number} decimals The number of decimal places to format the number. Defaults to 2.
	 * @returns {string} The formatted number as a string.
	 */
	protected formatNumber(value: string, decimals = 2) {
		return formatNumber(value, decimals);
	}

	/**
	 * Formats a given string value as a number with its implicit unit or an optional unit.
	 * @param {string} value The value to format.
	 * @param {number} [decimals=2] The number of decimal places to round to.
	 * @param {string} [unit] The unit to append to the formatted number.
	 * @returns {string} The formatted number with the unit (if provided).
	 */
	protected formatNumberWithUnit(value: string, decimals = 2, unit?: string) {
		value = value.trim();
		const formattedNumber = formatNumber(value, decimals) ?? '';
		const defaultUnit = value.includes(' ') ? value.slice(value.indexOf(' ') + 1) : '';
		const finalUnit = unit || defaultUnit;

		if (!formattedNumber.length) {
			return '';
		}

		return finalUnit ? `${formattedNumber} ${finalUnit}` : formattedNumber;
	}

	/**
	 * Converts the given string to sentence case.
	 * @param {string} str The string to convert to sentence case.
	 * @returns {string} The converted string in sentence case.
	 */

	protected toSentenceCase(str: string) {
		return toSentenceCase(str);
	}

	/**
	 * Checks if a given value has a specified uppercased prefix.
	 * @param value - The value to check.
	 * @param prefix - The prefix to compare against.
	 * @returns A boolean indicating whether the value has the specified prefix.
	 */
	protected hasPrefix(value: string, prefix: string) {
		return value.substring(0, prefix.length).toUpperCase() == prefix.toUpperCase();
	}

	/**
	 * Merges multiple data objects by overwriting with non-null values and
	 * latest object attribute with value
	 */
	private mergeDataObjects(dataArray: Model[]): Model {
		return dataArray.reduce(
			(merged: Model, current: Model) => {
				return {
					...merged,
					...current,
				};
			},
			{ ...dataArray[0] },
		);
	}
}
